/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect, useState } from 'react';
import {
	Box,
	Progress,
	Image,
	rem,
	Text,
} from '@mantine/core';
import classes from './SingleFileUploadWithProgress.module.css';

// Icons
import SheetIcon from '@assets/images/sheet-file-icon.svg';
import CancelUploadIcon from '@assets/images/cancel-upload-icon.svg';
import ReloadIcon from '@assets/icons/reload-icon.svg';
import { IconCircleCheckFilled } from '@tabler/icons-react';

// Utils
import { truncateString } from '@utils/functions';
import { Tooltip } from '@components/atoms/Tooltip';

export function uploadFile(
	onProgress: (percentage: number) => void
) {
	onProgress(Math.round(100));
}

export interface SingleFileUploadWithProgressProps {
	file: any;
	onDelete: (file: File) => void;
	onUpload: (file: File, url: string) => void;
	onReload: () => void;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const SingleFileUploadWithProgress = ({
	file,
	onDelete,
	onUpload,
	onReload,
}: SingleFileUploadWithProgressProps) => {
	const [progress, setProgress] = useState(0);
	useEffect(() => {
		async function upload() {
			await uploadFile(setProgress);
			onUpload(file, 'url');
		}

		upload();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<Box className={classes.container}>
			<Box className={classes.wrapper}>
				<Image
					style={{
						width: rem(29),
						height: rem(29),
						color: 'var(--mantine-color-dimmed)',
					}}
					src={SheetIcon}
				/>
				<Tooltip
					isActive={file.path.length > 24}
					label={file.path}
				>
					<Text className={classes.filename}>
						{truncateString(file.path, 24)}
					</Text>
				</Tooltip>
				<Box className={classes.containerProgress}>
					<Progress.Root
						size="md"
						className={classes.progressRoot}
					>
						<Progress.Section
							color="success"
							value={progress}
							style={{
								borderRadius: '1px',
							}}
						/>
					</Progress.Root>
					{progress == 100 && (
						<IconCircleCheckFilled
							style={{
								width: rem(30),
								height: rem(30),
								color: 'var(--mantine-color-success-9)',
							}}
							stroke={1.5}
						/>
					)}
					{progress !== 100 && (
						<Image
							data-testid="D2LtDXR7_Z5jdHi11-button"
							onClick={() => {
								onDelete(file);
							}}
							className={classes.cancelUpload}
							src={CancelUploadIcon}
						/>
					)}
					{progress == 100 && (
						<Image
							data-testid="D2EtDXR7_Z5jdHi6B-dropzone"
							className={classes.reloadRowIcon}
							src={ReloadIcon}
							onClick={() => {
								onReload();
							}}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default SingleFileUploadWithProgress;
